import { getChatApiRoute } from '@shared/utils/ChatBackendUtils'
import { events } from 'fetch-event-stream'
import GlobalLogger from '@shared/utils/logger/GlobalLogger.ts'

const log = GlobalLogger.bind('AudioNoteSummaryFetch')

export interface SummaryFetchCallbacks {
  onStreamEvent: (summary: string) => void
  /** `httpStatus` is set when the route itself refused the request (e.g. 403 for a free plan). */
  onError: (error: string, httpStatus?: number) => void
  onSuccess: () => void
}

/**
 * Abort the summarize request if the SSE stream goes silent for this long.
 * Without a deadline a stalled stream parks the await forever and the note
 * stays in "generating summary" with no error and no log trail. The timer
 * resets on every received event, so slow-but-alive streams are unaffected.
 */
export const SUMMARY_STREAM_INACTIVITY_TIMEOUT_MS = 180_000

/**
 * Rejoin budget for a stream that drops before the server confirms completion. The
 * backend resumes an interrupted generation on its next Durable Object instance, so
 * rejoining under the same generation picks the run back up rather than surfacing an error.
 */
export const SUMMARY_STREAM_MAX_ATTEMPTS = 3
export const SUMMARY_STREAM_REJOIN_DELAY_MS = 1_000

type StreamOutcome =
  | { type: 'completed'; title: string; summary: string }
  | { type: 'error'; error: string; httpStatus?: number }
  | { type: 'disconnected' }

/**
 * Fetch and stream audio note summary from the backend
 * Returns the final title and summary, or null on error
 */
export async function fetchAudioNoteSummary(
  audioNoteId: string,
  accessToken: string,
  callbacks: SummaryFetchCallbacks,
  generationId?: string,
): Promise<{ title: string; summary: string } | null> {
  for (let attempt = 1; ; attempt++) {
    log.info(
      `Opening summary stream for audio note ${audioNoteId} (attempt=${attempt}, generationId=${generationId ?? 'default'})`,
    )
    const outcome = await streamSummaryOnce(audioNoteId, accessToken, callbacks.onStreamEvent, generationId)
    if (outcome.type === 'completed') {
      log.info(`Summary stream completed for audio note ${audioNoteId} (${outcome.summary.length} chars)`)
      callbacks.onSuccess()
      return { title: outcome.title, summary: outcome.summary }
    }
    if (outcome.type === 'disconnected' && attempt < SUMMARY_STREAM_MAX_ATTEMPTS) {
      log.warn(
        `Summary stream for audio note ${audioNoteId} dropped before completing; rejoining (attempt ${attempt + 1})`,
      )
      await new Promise((resolve) => setTimeout(resolve, SUMMARY_STREAM_REJOIN_DELAY_MS * attempt))
      continue
    }
    if (outcome.type === 'error') {
      if (outcome.httpStatus !== undefined) {
        callbacks.onError(outcome.error, outcome.httpStatus)
      } else {
        callbacks.onError(outcome.error)
      }
    } else {
      log.error(
        `Summary stream for audio note ${audioNoteId} exhausted rejoin attempts without completion (attempts=${attempt})`,
      )
      callbacks.onError('Error generating summary.')
    }
    return null
  }
}

async function streamSummaryOnce(
  audioNoteId: string,
  accessToken: string,
  onStreamEvent: SummaryFetchCallbacks['onStreamEvent'],
  generationId?: string,
): Promise<StreamOutcome> {
  const abort = new AbortController()
  let inactivityTimer: ReturnType<typeof setTimeout> | undefined
  const resetInactivityTimer = () => {
    clearTimeout(inactivityTimer)
    inactivityTimer = setTimeout(() => {
      log.error(
        `Summary stream for audio note ${audioNoteId} silent for ${SUMMARY_STREAM_INACTIVITY_TIMEOUT_MS}ms — aborting`,
      )
      abort.abort()
    }, SUMMARY_STREAM_INACTIVITY_TIMEOUT_MS)
  }

  try {
    resetInactivityTimer()
    const query = generationId ? `?idempotencyKey=${encodeURIComponent(generationId)}` : ''
    const response = await fetch(getChatApiRoute(`api/v1/audio-notes/${audioNoteId}/summarize${query}`), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      signal: abort.signal,
    })
    // Headers received — give the wait for the first event its own budget
    // rather than sharing it with the connection phase.
    resetInactivityTimer()

    if (!response.ok) {
      const error = await response.text()
      log.error(`Error generating summary for audio note ${audioNoteId}: ${error}`)
      return { type: 'error', error: 'Error generating summary.', httpStatus: response.status }
    }

    const stream = events(response, abort.signal)
    let title = ''
    let summary = ''

    for await (const event of stream) {
      resetInactivityTimer()
      if (!event.data) continue

      let parsedEventData:
        | {
            type: 'data' | 'completed'
            title: string
            summary: string
          }
        | {
            type: 'error'
            error: string
          }

      try {
        parsedEventData = JSON.parse(event.data)
      } catch (err) {
        log.error(`Error parsing event data: ${err}`)
        continue
      }

      if (parsedEventData.type === 'error') {
        log.error(`Error generating summary for audio note ${audioNoteId}: ${parsedEventData.error}`)
        return { type: 'error', error: parsedEventData.error }
      }

      title = parsedEventData.title
      summary = parsedEventData.summary

      // Update streaming summary via callback
      onStreamEvent(summary)
      if (parsedEventData.type === 'completed') {
        return { type: 'completed', title, summary }
      }
    }

    // EOF can mean a broken connection; only the server confirms persistence.
    return abort.signal.aborted ? { type: 'error', error: 'Error generating summary.' } : { type: 'disconnected' }
  } catch (error) {
    log.error(`Error generating summary for audio note ${audioNoteId}: ${error}`)
    if (error instanceof Error && error.message.includes('sign up')) {
      return { type: 'error', error: "You'll need to sign in to generate more summaries." }
    }
    return abort.signal.aborted ? { type: 'error', error: 'Error generating summary.' } : { type: 'disconnected' }
  } finally {
    clearTimeout(inactivityTimer)
    abort.abort()
  }
}
